import React, { useState, useEffect } from 'react';
import Button from '../../../components/Button';
import { ArrowLeft, Camera, Check, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { useAudience } from '../../../context/AudienceContext';
import { useAuth } from '../../../context/AuthContext';
import ParsedQuestionsPreview from '../../../components/survey/ParsedQuestionsPreview';
import useQuestionParsingSocket from '../../../hooks/useQuestionParsingSocket';
import useRealtimeQuestionTags from '../../../hooks/useRealtimeQuestionTags';
import { createSimulation, handleApiError } from '../../../utils/simulationApi';
import { ParsedQuestion } from '../../../components/survey/types';

interface SegmentPersonaFilters {
  industryL1: string[];
  industryL2: string[];
  functions: string[];
  roles: string[];
}

// Helper to check if initial parsed questions have validation errors
const checkInitialValidationErrors = (questions: ParsedQuestion[]): boolean => {
  if (!questions || questions.length === 0) return false;

  for (const q of questions) {
    const type = q.type;

    // Choice-based questions require at least 2 choices
    if (['single_choice', 'multiple_choice', 'dropdown', 'ranking'].includes(type)) {
      if (!q.choices || q.choices.length < 2) return true;
      if (q.choices.some(c => !c || !c.trim())) return true;
    }

    // Matrix questions require at least 1 row
    if (['matrix_grid', 'matrix_rating'].includes(type)) {
      if (!q.matrix_rows || q.matrix_rows.length < 1) return true;
      if (q.matrix_rows.some(r => !r.trim())) return true;
    }

    // MaxDiff requires at least 4 items
    if (type === 'max_diff') {
      if (!q.maxdiff_items || q.maxdiff_items.length < 4) return true;
    }

    // Conjoint requires at least 2 attributes
    if (type === 'conjoint') {
      if (!q.conjoint_attributes || q.conjoint_attributes.length < 2) return true;
    }
  }

  return false;
};

interface InsightsFormProps {
  onSubmit: (simulationId: number) => void;
  selectedSegmentIds: number[];
  personaFilters: Record<number, SegmentPersonaFilters>;
  onBack: () => void;
  onEditStep?: () => void;
  initialFormData?: Record<string, any>;
  onFormDataChange?: (data: Record<string, any>) => void;
  onSavePayload?: (payload: Record<string, any>) => void;
  // Dynamic segmentation support
  dynamicSegmentationId?: number | null;
  dynamicSegmentIndices?: number[];
}

const SurveyAndFocusGroups: React.FC<InsightsFormProps> = ({
  onSubmit,
  selectedSegmentIds,
  personaFilters,
  onBack,
  onEditStep,
  initialFormData = {},
  onFormDataChange = () => {},
  onSavePayload,
  dynamicSegmentationId,
  dynamicSegmentIndices = [],
}) => {
  const { audienceData } = useAudience();
  const { isAdmin } = useAuth();

  const [simName, setSimName] = useState(initialFormData.simName || '');
  const [goal, setGoal] = useState(initialFormData.goal || '');
  const [questionsText, setQuestionsText] = useState(initialFormData.questionsText || '');
  const [context, setContext] = useState(initialFormData.context || '');
  const [useInternalDebate, setUseInternalDebate] = useState(initialFormData.useInternalDebate || false);
  const [enableAdaptiveContext, setEnableAdaptiveContext] = useState(initialFormData.enableAdaptiveContext || false);
  const [useTwoPassParsing, setUseTwoPassParsing] = useState(initialFormData.useTwoPassParsing ?? true);
  
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [parsedQuestions, setParsedQuestions] = useState<ParsedQuestion[]>(
    initialFormData.parsedQuestions || []
  );
  const [previewDismissed, setPreviewDismissed] = useState(false);
  // Initialize with actual validation state from initial parsed questions
  const [hasValidationErrors, setHasValidationErrors] = useState(() =>
    checkInitialValidationErrors(initialFormData.parsedQuestions || [])
  );
  const [showQuestionGuide, setShowQuestionGuide] = useState(false);

  const [questionImages, setQuestionImages] = useState<Map<number, Array<{path: string, url: string, file: File}>>>(new Map());

  // --- Celery fallback for large / image-heavy surveys ---
  const handleParsingCompleted = React.useCallback((data: any) => {
    const qs: ParsedQuestion[] = data.parsed_questions || [];
    setParsedQuestions(qs);
    setPreviewDismissed(false);
  }, []);

  const handleParsingFailed = React.useCallback((data: any) => {
    console.error('Question parsing failed:', data);
    setError(data.error || data.detail || 'Failed to parse questions');
  }, []);

  const {
    parsingState,
    triggerParsing,
    clearParsingState,
    connect
  } = useQuestionParsingSocket({
    onParsingCompleted: handleParsingCompleted,
    onParsingFailed: handleParsingFailed,
    autoConnect: false
  });

  // --- Realtime parsing (always active unless Celery is running) ---
  const {
    parsedQuestions: realtimeParsed,
    isParsing,
    isUpdating,
    error: realtimeError,
    seedCache,
    reset: resetRealtimeHook,
    loadingIndices,
  } = useRealtimeQuestionTags(
    questionsText,
    goal,
    context,
    !parsingState.isParsing,
    useTwoPassParsing,
  );

  // Sync realtime results into local state (no clearing on each keystroke)
  useEffect(() => {
    if (realtimeParsed.length > 0) {
      setParsedQuestions(realtimeParsed);
      setPreviewDismissed(false);
    } else if (!isParsing && !isUpdating && questionsText.trim() === '') {
      setParsedQuestions([]);
    }
  }, [realtimeParsed, isParsing, isUpdating, questionsText]);

  // Surface realtime parsing errors (e.g. raw_text length cap, rate limit)
  // to the form-level error banner so respondents/authors aren't stranded
  // on a silently-stuck textarea. We track the last realtime message we
  // wrote so we only clear the banner when the user fixes the issue, never
  // when another source (Celery / submit) populated it.
  const lastRealtimeErrorRef = React.useRef<string | null>(null);
  useEffect(() => {
    if (realtimeError) {
      setError(realtimeError);
      lastRealtimeErrorRef.current = realtimeError;
    } else if (lastRealtimeErrorRef.current) {
      const lastWritten = lastRealtimeErrorRef.current;
      lastRealtimeErrorRef.current = null;
      setError((prev) => (prev === lastWritten ? null : prev));
    }
  }, [realtimeError]);

  // Keep the hook's cache in sync with local edits so re-parses preserve them
  // Skip seeding if any questions are still loading (skeletons) to avoid cache corruption
  useEffect(() => {
    if (parsedQuestions.length > 0) {
      const hasLoadingQuestions = parsedQuestions.some((q: any) => q.isLoading);
      if (!hasLoadingQuestions) {
        seedCache(parsedQuestions);
      }
    }
  }, [parsedQuestions, seedCache]);

  // Update parent with form data changes
  useEffect(() => {
    onFormDataChange({
      simName, goal, questionsText, context, useInternalDebate, enableAdaptiveContext, useTwoPassParsing
    });
  }, [simName, goal, questionsText, context, useInternalDebate, enableAdaptiveContext, useTwoPassParsing]);

  const handleFieldChange = (
    setter: React.Dispatch<React.SetStateAction<string>>, 
    value: string
  ) => {
    setter(value);
    if (onEditStep) onEditStep();
  };

  // Initialize images from initial data (edit & rerun)
  useEffect(() => {
    if (initialFormData.parsedQuestions && Array.isArray(initialFormData.parsedQuestions)) {
      const imageMap = new Map<number, Array<{path: string, url: string, file: File}>>();
      initialFormData.parsedQuestions.forEach((question: any, index: number) => {
        if (question.image_paths?.length > 0) {
          const images = question.image_paths.map((imagePath: string, imgIdx: number) => {
            let pathForComponent = imagePath;
            if (imagePath.startsWith('http')) {
              try { pathForComponent = new URL(imagePath).pathname; } catch { /* keep original */ }
            }
            return {
              path: imagePath,
              url: pathForComponent,
              file: new File([], `existing-image-${index}-${imgIdx}`, { type: 'image/jpeg' })
            };
          });
          imageMap.set(index, images);
        }
      });
      if (imageMap.size > 0) setQuestionImages(imageMap);
    }
  }, []);

  const saveMode = !!onSavePayload;
  const totalImageCount = Array.from(questionImages.values()).reduce((sum, imgs) => sum + imgs.length, 0);
  const questionsWithImages = questionImages.size;
  const showPreview = parsedQuestions.length > 0 && !previewDismissed;
  // Check if we have segments selected (either static or dynamic)
  const hasSegmentsSelected = selectedSegmentIds.length > 0 || dynamicSegmentIndices.length > 0;
  const isFormValid = questionsText.trim() !== ''
    && (saveMode || hasSegmentsSelected)
    && (!showPreview || !hasValidationErrors);

  // Celery fallback: trigger when images attached
  const triggerCeleryFallback = async () => {
    try {
      clearParsingState();
      setError(null);
      if (!parsingState.isConnected) await connect();

      const questions = parsedQuestions.length > 0
        ? parsedQuestions.map(q => q.text)
        : [questionsText.trim()].filter(Boolean);
      if (questions.length === 0) { setError('Please enter at least one question'); return; }

      const questionTypes: Record<number, string> = {};
      parsedQuestions.forEach((q, i) => { if (q.type) questionTypes[i] = q.type; });

      const imagePaths: Record<number, string[]> = {};
      questionImages.forEach((imgs, idx) => { imagePaths[idx] = imgs.map(img => img.path); });

      await triggerParsing(
        questions, goal, context,
        Object.keys(imagePaths).length > 0 ? imagePaths : undefined,
        false,
        Object.keys(questionTypes).length > 0 ? questionTypes : undefined,
        'ai_sim',
        useTwoPassParsing,
      );
    } catch (err) {
      console.error('Error triggering Celery parsing:', err);
      setError(err instanceof Error ? err.message : 'Failed to parse questions');
    }
  };

  const handleQuestionsUpdated = (questions: ParsedQuestion[]) => {
    setParsedQuestions(questions);
    seedCache(questions);
  };

  const handleUseParsedQuestions = (questions: ParsedQuestion[]) => {
    setParsedQuestions(questions);
    seedCache(questions);
  };

  const handleBackToQuestions = () => {
    setPreviewDismissed(true);
    resetRealtimeHook();
    clearParsingState();
  };

  const handleStartSimulation = async (questions: ParsedQuestion[]) => {
    const simulationData: Record<string, any> = {
      audience_id: audienceData.audienceId,
      segment_ids: selectedSegmentIds,
      name: simName,
      persona_filters: personaFilters,
      task: 'survey_and_focus_groups',
      questions: questions,
      context: context,
      goal: goal,
      use_parsed_questions: true,
      use_internal_debate: useInternalDebate,
      enable_adaptive_persona_context: enableAdaptiveContext
    };
    // Add dynamic segmentation data if present
    if (dynamicSegmentationId) {
      simulationData.dynamic_segmentation_id = dynamicSegmentationId;
      simulationData.dynamic_segment_indices = dynamicSegmentIndices;
    }
    if (onSavePayload) { onSavePayload(simulationData); return; }
    const data = await createSimulation(simulationData);
    onSubmit(data.simulation_id);
  };

  const handleSubmit = async () => {
    if (!isFormValid || isSubmitting) return;

    if (!showPreview) {
      if (totalImageCount > 0) {
        await triggerCeleryFallback();
      }
      return;
    }

    setIsSubmitting(true);
    try {
      const questionsForSim = parsedQuestions.filter(q => q.type !== 'info_screen');
      const simulationData: Record<string, any> = {
        audience_id: audienceData.audienceId,
        segment_ids: selectedSegmentIds,
        name: simName,
        persona_filters: personaFilters,
        task: 'survey_and_focus_groups',
        questions: questionsForSim,
        context: context,
        goal: goal,
        use_parsed_questions: true,
        use_internal_debate: useInternalDebate,
        enable_adaptive_persona_context: enableAdaptiveContext
      };
      // Add dynamic segmentation data if present
      if (dynamicSegmentationId) {
        simulationData.dynamic_segmentation_id = dynamicSegmentationId;
        simulationData.dynamic_segment_indices = dynamicSegmentIndices;
      }
      if (onSavePayload) { onSavePayload(simulationData); return; }
      const data = await createSimulation(simulationData);
      onSubmit(data.simulation_id);
    } catch (err) {
      console.error('Error starting simulation:', err);
      handleApiError(err, setError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            icon={<ArrowLeft className="w-4 h-4 mr-1" />}
          >
            Back to use case selection
          </Button>
          <span className="text-sm text-gray-500">
            {dynamicSegmentIndices.length > 0
              ? `${dynamicSegmentIndices.length} dynamic segment${dynamicSegmentIndices.length !== 1 ? 's' : ''} selected`
              : `${selectedSegmentIds.length} segment${selectedSegmentIds.length !== 1 ? 's' : ''} selected`}
          </span>
        </div>
        {error && <div className="text-sm text-red-600 mb-3">{error}</div>}
      </div>

      <div className="mb-6">
        <label htmlFor="simName" className="block text-sm font-medium text-gray-700 mb-2">
          Simulation Name
        </label>
        <input
          id="simName"
          type="text"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          value={simName}
          onChange={(e) => handleFieldChange(setSimName, e.target.value)}
          placeholder="Enter a name for your simulation"
        />
      </div>

      <div className="mb-6">
        <label htmlFor="goal" className="block text-sm font-medium text-gray-700 mb-2">
          Goal
        </label>
        <input
          id="goal"
          type="text"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          value={goal}
          onChange={(e) => handleFieldChange(setGoal, e.target.value)}
          placeholder="What do you want to achieve with this simulation?"
        />
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <label htmlFor="questionsText" className="block text-sm font-medium text-gray-700">
            Survey or Focus Group Questions
          </label>
          <div className="flex items-center gap-3">
            {totalImageCount > 0 && (
              <span className="flex items-center text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                <Camera className="w-3 h-3 mr-1" />
                {totalImageCount} {totalImageCount === 1 ? 'image' : 'images'} across {questionsWithImages} {questionsWithImages === 1 ? 'question' : 'questions'}
              </span>
            )}
            {showPreview && !isParsing && !isUpdating && (
              <div className="flex items-center gap-1">
                <Check className="w-3 h-3 text-green-500" />
                <span className="text-xs text-green-600 font-medium">
                  {parsedQuestions.filter(q => q.type !== 'info_screen').length} question{parsedQuestions.filter(q => q.type !== 'info_screen').length !== 1 ? 's' : ''} parsed
                </span>
              </div>
            )}
            {isUpdating && !isParsing && (
              <span className="text-xs text-blue-600 font-medium">Re-parsing...</span>
            )}
          </div>
        </div>

        {/* Supported question types - compact badges */}
        <div className="mb-2 flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-gray-500">Supported:</span>
          {[
            { label: 'Single Choice', bg: 'bg-blue-50', text: 'text-blue-600' },
            { label: 'Multi Choice', bg: 'bg-indigo-50', text: 'text-indigo-600' },
            { label: 'Yes/No', bg: 'bg-sky-50', text: 'text-sky-600' },
            { label: 'Rating', bg: 'bg-purple-50', text: 'text-purple-600' },
            { label: 'Likert', bg: 'bg-violet-50', text: 'text-violet-600' },
            { label: 'NPS', bg: 'bg-orange-50', text: 'text-orange-600' },
            { label: 'Ranking', bg: 'bg-fuchsia-50', text: 'text-fuchsia-600' },
            { label: 'Matrix', bg: 'bg-lime-50', text: 'text-lime-600' },
            { label: 'Open Text', bg: 'bg-emerald-50', text: 'text-emerald-600' },
            { label: 'MaxDiff', bg: 'bg-rose-50', text: 'text-rose-600' },
            { label: 'Conjoint', bg: 'bg-pink-50', text: 'text-pink-600' },
          ].map(t => (
            <span key={t.label} className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${t.bg} ${t.text}`}>
              {t.label}
            </span>
          ))}
        </div>

        <textarea
          id="questionsText"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          rows={6}
          value={questionsText}
          onChange={(e) => handleFieldChange(setQuestionsText, e.target.value)}
          placeholder="Type your questions freely — AI will parse them into structured survey questions automatically"
        />

        {!showPreview && !isParsing && questionsText.trim() !== '' && (
          <p className="mt-2 text-xs text-gray-500">
            Type your questions — AI will automatically split, classify, and extract options, scales, and more.
          </p>
        )}

        {/* Collapsible Question Guide */}
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setShowQuestionGuide(!showQuestionGuide)}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors"
          >
            <HelpCircle className="w-3.5 h-3.5" />
            <span>Question writing guide</span>
            {showQuestionGuide ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>

          {showQuestionGuide && (
            <div className="mt-2 p-4 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-600 space-y-3">
              <div>
                <h4 className="font-semibold text-gray-700 mb-1.5">Tips for Better Results</h4>
                <ul className="space-y-1">
                  <li><span className="text-gray-400">•</span> <strong>Include answer options</strong> — e.g., "How satisfied? Very/Somewhat/Not at all"</li>
                  <li><span className="text-gray-400">•</span> <strong>Specify scales</strong> — e.g., "Rate 1-5" or "On a scale of 0-10"</li>
                  <li><span className="text-gray-400">•</span> <strong>Be specific</strong> — "What features do you use?" vs "Tell me about usage"</li>
                  <li><span className="text-gray-400">•</span> <strong>One question per line</strong> works best for clear separation</li>
                  <li><span className="text-gray-400">•</span> <strong>Avoid leading questions</strong> — "What do you think?" not "Don't you agree?"</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-gray-700 mb-1.5">Example Formats</h4>
                <div className="bg-white border border-gray-200 rounded p-2 font-mono text-[11px] space-y-1">
                  <div className="text-gray-500"># Single choice with options:</div>
                  <div>How often do you exercise? Daily / Weekly / Monthly / Rarely</div>
                  <div className="text-gray-500 mt-2"># Rating scale:</div>
                  <div>Rate your satisfaction (1-5)</div>
                  <div className="text-gray-500 mt-2"># Open-ended:</div>
                  <div>What improvements would you suggest?</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Single parsing indicator for both initial parse and re-parse */}
        {(isParsing || isUpdating) && (
          <div className="mt-4 flex items-center gap-3 px-4 py-3 bg-blue-50 border border-blue-100 rounded-lg">
            <div className="flex gap-1">
              <span className="block w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '0ms', animationDuration: '1s' }} />
              <span className="block w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '150ms', animationDuration: '1s' }} />
              <span className="block w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '300ms', animationDuration: '1s' }} />
            </div>
            <span className="text-xs font-medium text-blue-700">
              {isUpdating ? 'AI is re-parsing questions...' : 'AI is parsing questions...'}
            </span>
          </div>
        )}

        {/* Celery fallback progress */}
        {parsingState.isParsing && (
          <div className="mt-3 p-3 bg-blue-50 rounded-md">
            <div className="flex items-center space-x-3">
              <div className="flex-1">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-blue-700">Parsing with images...</span>
                  <span className="text-blue-600">{parsingState.progress}%</span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${parsingState.progress}%` }}
                  />
                </div>
                {parsingState.currentStep && (
                  <p className="text-xs text-blue-600 mt-1">{parsingState.currentStep}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Inline parsed questions preview -- stays visible during re-parse */}
        {showPreview && (
          <div className="mt-4">
            <ParsedQuestionsPreview
              parsedQuestions={parsedQuestions}
              onQuestionsUpdated={handleQuestionsUpdated}
              onUseQuestions={handleUseParsedQuestions}
              onStartSimulation={handleStartSimulation}
              onGoBack={handleBackToQuestions}
              onValidationChange={setHasValidationErrors}
              isLoading={false}
              saveMode={saveMode}
              loadingIndices={loadingIndices}
            />
          </div>
        )}
      </div>

      <div className="mb-6">
        <label htmlFor="context" className="block text-sm font-medium text-gray-700 mb-2">
          Context (Optional)
        </label>
        <textarea
          id="context"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          rows={3}
          value={context}
          onChange={(e) => handleFieldChange(setContext, e.target.value)}
          placeholder="Any additional context about what you're trying to learn"
        />
      </div>

      {isAdmin && (
        <>
          <div className="mb-6">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={useInternalDebate}
                onChange={(e) => setUseInternalDebate(e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Enable Internal Debate</span>
            </label>
            <p className="mt-1 text-xs text-gray-500 ml-6">
              When enabled, each persona will go through an internal Proponent-Skeptic-Judge debate to generate more nuanced responses.
            </p>
          </div>

          <div className="mb-6">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={enableAdaptiveContext}
                onChange={(e) => setEnableAdaptiveContext(e.target.checked)}
                className="w-4 h-4 text-[#07E5D1] bg-gray-100 border-gray-300 rounded focus:ring-[#07E5D1] focus:ring-2"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Enable Adaptive Persona Context</span>
            </label>
            <p className="mt-1 text-xs text-gray-500 ml-6">
              Analyzes each question to identify relevant persona fields, optimizes prompts, and validates if persona data is sufficient. Shows confidence scores in results.
            </p>
          </div>

          <div className="mb-6">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={useTwoPassParsing}
                onChange={(e) => setUseTwoPassParsing(e.target.checked)}
                className="w-4 h-4 text-[#07E5D1] bg-gray-100 border-gray-300 rounded focus:ring-[#07E5D1] focus:ring-2"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Enable Two-Pass Parsing (Experimental)</span>
            </label>
            <p className="mt-1 text-xs text-gray-500 ml-6">
              Uses fast Haiku two-pass parsing: first detects question boundaries, then parses each in parallel for faster processing.
            </p>
          </div>
        </>
      )}

      {/* Bottom CTA -- always at the bottom of the form */}
      <div className="flex justify-between items-center">
        <div>{error && <p className="text-sm text-red-600">{error}</p>}</div>
        <Button
          variant="primary"
          size="lg"
          onClick={handleSubmit}
          disabled={!isFormValid || isSubmitting || parsingState.isParsing || isParsing}
          className="whitespace-nowrap"
        >
          {parsingState.isParsing ? 'Parsing...' : isParsing ? 'Detecting...' : isSubmitting ? (saveMode ? 'Saving...' : 'Starting...') : saveMode ? 'Continue' : 'Start Simulation'}
        </Button>
      </div>
    </div>
  );
};

export default SurveyAndFocusGroups;
